import { CARD_HEIGHT, CARD_WIDTH, Game } from '@phaser/scenes/Game';
import { CardObject } from '@phaser/objects/Card.object';

export class AttackBehavior {
  private _warningBar?: Phaser.GameObjects.Rectangle;
  private _warningBg?: Phaser.GameObjects.Rectangle;
  private _border?: Phaser.GameObjects.Graphics;
  private _attackCounter?: Phaser.Tweens.Tween;
  private _scene: Game = Game.current;
  private _isAttacking = false;

  constructor(private _card: CardObject) {
    this._startAttackTimer();
  }

  public attack({ resetTimer }: { resetTimer: boolean }) {
    if (this._isAttacking) return;
    this._isAttacking = true;

    const cY = this._card.container.y;

    // Lunge forward — fast, aggressive
    this._scene.tweens.add({
      y: [cY, cY - 4, cY - 6, cY - 6, cY + 55, cY],
      targets: this._card.tweenTargets,
      interpolation: 'catmullrom',
      ease: 'Power2',
      duration: 320,
      onComplete: () => {
        this._isAttacking = false;
      },
    });

    this._flashFire();

    if (resetTimer) this._startAttackTimer();
  }

  public updateInterval() {
    this._startAttackTimer();
  }

  public destroy() {
    this._attackCounter?.stop();
    this._warningBar?.destroy();
    this._warningBg?.destroy();
    this._border?.destroy();
  }

  private _flashFire() {
    const overlay = this._scene.add.graphics().setDepth(11);
    this._card.container.add(overlay);

    this._scene.tweens.addCounter({
      from: 0.85,
      to: 0,
      duration: 280,
      onUpdate: (t) => {
        overlay.clear();
        overlay.fillStyle(0xFF2200, t.getValue()!);
        overlay.fillRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT);
      },
      onComplete: () => overlay.destroy(),
    });
  }

  private _startAttackTimer() {
    this._attackCounter?.stop();
    this._warningBar?.destroy();
    this._warningBg?.destroy();
    this._border?.destroy();

    const duration = this._card.data.enemy?.attackInterval || 1000;

    // Warning bar background — sits above health bars
    this._warningBg = this._scene.add.rectangle(
      0, CARD_HEIGHT / 2 - 13, CARD_WIDTH, 4, 0x1a0000,
    );
    this._card.container.add(this._warningBg);

    // Warning bar — fills left to right over the attack interval
    this._warningBar = this._scene.add.rectangle(
      -CARD_WIDTH / 2, CARD_HEIGHT / 2 - 13, 0, 4, 0xFF2200,
    ).setOrigin(0, 0.5);
    this._card.container.add(this._warningBar);

    // Border glow graphic
    this._border = this._scene.add.graphics();
    this._card.container.add(this._border);

    this._attackCounter = this._scene.tweens.addCounter({
      onUpdate: (tween) => {
        const progress = tween.getValue()! / 360;

        if (!this._warningBar || !this._border) return;

        // Fill warning bar
        this._warningBar.width = CARD_WIDTH * progress;

        // Color: dark orange → bright red
        const g = Math.floor(Phaser.Math.Linear(80, 0, Math.min(progress * 1.6, 1)));
        this._warningBar.fillColor = Phaser.Display.Color.GetColor(255, g, 0);

        // Border glow builds after 55% progress
        this._border.clear();
        if (progress > 0.55) {
          const bp = (progress - 0.55) / 0.45;
          const alpha = Phaser.Math.Linear(0, 0.8, bp);
          const lineW = Phaser.Math.Linear(1, 3.5, bp);
          this._border.lineStyle(lineW, 0xFF1100, alpha);
          this._border.strokeRect(
            -CARD_WIDTH / 2 - 1, -CARD_HEIGHT / 2 - 1,
            CARD_WIDTH + 2, CARD_HEIGHT + 2,
          );

          // Inner red tint builds at 85%+
          if (bp > 0.65) {
            const innerAlpha = Phaser.Math.Linear(0, 0.15, (bp - 0.65) / 0.35);
            this._border.fillStyle(0xFF0000, innerAlpha);
            this._border.fillRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT);
          }
        }
      },
      onComplete: () => {
        this._warningBar?.destroy();
        this._warningBg?.destroy();
        this._border?.destroy();
        this._warningBar = undefined;
        this._warningBg = undefined;
        this._border = undefined;
      },
      duration,
      from: 0,
      to: 360,
    });
  }
}
